// DailySales.jsx

import React, { useEffect, useState, useContext } from "react";
import api from "../../../../api/api";
import Swal from "sweetalert2";
import dayjs from "dayjs";

import {
  Card,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Grid,
  Typography,
  CircularProgress,
  Pagination,
} from "@mui/material";

import { pdf } from "@react-pdf/renderer";

import { useYear } from "../../../../api/context/YearContext";
import { AuthContext } from "../../../../AuthContext";

import DailySalesPDFDocument from "./PraintlDailySale";

import "./DailySales.css";

const DailySales = () => {
  const { selectedYear } = useYear();
  const { user } = useContext(AuthContext);

  const today = dayjs().format("YYYY-MM-DD");

  const [loading, setLoading] = useState(false);
  const [sales, setSales] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [date, setDate] = useState(today);
  const [isLocked, setIsLocked] = useState(false);

  const canGenerateReport = ["admin", "sales_agent"].includes(user?.user_type);

  // ================= SAFE FORMAT =================
  const formatMoney = (value) =>
    Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // ================= STATUS =================
  const checkStatus = async () => {
    try {
      const res = await api.get("/daily-sales/status/");
      setIsLocked(res.data.is_locked);
    } catch (err) {
      console.log(err);
    }
  };

  // ================= FETCH =================
  const fetchSales = async (pageNumber = 1, selectedDate = date) => {
    setLoading(true);

    try {
      const res = await api.get("/daily-sales/", {
        params: {
          page: pageNumber,
          date: selectedDate,
          year: selectedYear?.id,
        },
      });

      setSales(res.data.results || []);
      setTotalPages(Math.ceil((res.data.count || 0) / 8));
    } catch (err) {
      Swal.fire("Error", "Failed to fetch daily sales.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ================= GENERATE =================
  const generateReport = async () => {
    setLoading(true);

    try {
      const res = await api.post("/daily-sales/generate/", {
        year: selectedYear?.id,
      });

      Swal.fire("Success", res.data.message, "success");

      await checkStatus();
      await fetchSales(1, date);
      setPage(1);
    } catch (err) {
      Swal.fire(
        "Warning",
        err.response?.data?.message || "No sales found!",
        "warning"
      );
    } finally {
      setLoading(false);
    }
  };

  // ================= PRINT =================
  const printDailySales = async () => {
    try {
      setLoading(true);

      const res = await api.get("/daily-sales/", {
        params: {
          date,
          year: selectedYear?.id,
          page_size: 100000,
        },
      });

      const reportSales = res.data.results || [];

      const allItems = reportSales.flatMap((sale) => sale.items || []);

      if (allItems.length === 0) {
        Swal.fire("No Sales Found", `No sales for ${date}`, "warning");
        return;
      }

      const purchasedBy =
        allItems[0]?.purchased_by_name || "Multiple Customers";

      const blob = await pdf(
        <DailySalesPDFDocument
          sales={allItems}
          selectedDate={date}
          purchasedBy={purchasedBy}
        />
      ).toBlob();

      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");

      Swal.fire("Success", "PDF generated", "success");
    } catch (err) {
      Swal.fire("Error", "Failed to generate PDF", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
  }, []);

  useEffect(() => {
    if (!selectedYear) return;
    setPage(1);
    fetchSales(1, date);
  }, [selectedYear, date]);

  useEffect(() => {
    fetchSales(page, date);
  }, [page]);

  return (
    <Card className="daily-sales-card">

      <Typography variant="h5" className="title">
        Daily Sales Report - {selectedYear?.name}
      </Typography>

      <Grid container spacing={2} className="controls">

        {canGenerateReport && (
          <Grid item>
            <Button
              variant="contained"
              onClick={generateReport}
              disabled={isLocked}
            >
              {isLocked ? "Report Locked" : "Generate Report"}
            </Button>
          </Grid>
        )}

        <Grid item>
          <TextField
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setPage(1);
            }}
            size="small"
          />
        </Grid>

        <Grid item>
          <Button variant="outlined" onClick={() => fetchSales(1, date)}>
            View Records
          </Button>
        </Grid>

        <Grid item>
          <Button
            variant="contained"
            color="success"
            onClick={printDailySales}
          >
            Print Daily Report
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <div className="loader">
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table className="daily-sales-table">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Qty</TableCell>
                  <TableCell>Sold By</TableCell>
                  <TableCell>Unit Price</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sales.length > 0 ? (
                  sales.map((sale, saleIndex) =>
                    sale.items?.map((item, itemIndex) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          {(page - 1) * 8 + saleIndex + itemIndex + 1}
                        </TableCell>

                        <TableCell>{item.product_name || "-"}</TableCell>
                        <TableCell>{item.quantity_taken || 0}</TableCell>
                        <TableCell>{item.purchased_by_name || "-"}</TableCell>

                        <TableCell>
                          ${formatMoney(item.unit_price)}
                        </TableCell>

                        <TableCell>
                          ${formatMoney(item.total_price)}
                        </TableCell>

                        <TableCell>
                          {item.created_at
                            ? dayjs(item.created_at).format("YYYY-MM-DD")
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No records found for {date}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="pagination-container">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
            />
          </div>
        </>
      )}
    </Card>
  );
};

export default DailySales;


// // DailySales.jsx

// import React, { useEffect, useState, useContext } from "react";
// import api from "../../../../api/api";
// import Swal from "sweetalert2";
// import dayjs from "dayjs";

// import {
//   Card,
//   Button,
//   Table,
//   TableHead,
//   TableRow,
//   TableCell,
//   TableBody,
//   TextField,
//   Grid,
//   Typography,
//   CircularProgress,
//   Pagination,
// } from "@mui/material";

// import { pdf } from "@react-pdf/renderer";

// import { useYear } from "../../../../api/context/YearContext";
// import { AuthContext } from "../../../../AuthContext";

// import DailySalesPDFDocument from "./PraintlDailySale";

// import "./DailySales.css";

// const DailySales = () => {
//   // ================= CONTEXT =================
//   const { selectedYear } = useYear();
//   const { user } = useContext(AuthContext);

//   const today = dayjs().format("YYYY-MM-DD");

//   // ================= STATES =================
//   const [loading, setLoading] = useState(false);
//   const [sales, setSales] = useState([]);
//   const [totalPages, setTotalPages] = useState(1);
//   const [page, setPage] = useState(1);
//   const [date, setDate] = useState(today);
//   const [isLocked, setIsLocked] = useState(false);

//   // ================= PERMISSIONS =================
//   const canGenerateReport = ["admin", "sales_agent"].includes(user?.user_type);

//   // ================= FORMAT MONEY =================
//   const formatMoney = (value) => {
//     if (!value) return "0.00";

//     return Number(value).toLocaleString(undefined, {
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2,
//     });
//   };

//   // ================= STATUS =================
//   const checkStatus = async () => {
//     try {
//       const res = await api.get("/daily-sales/status/");
//       setIsLocked(res.data.is_locked);
//     } catch (err) {
//       console.log(err);
//     }
//   };

//   // ================= FETCH SALES =================
//   const fetchSales = async (pageNumber = 1, selectedDate = date) => {
//     setLoading(true);

//     try {
//       const res = await api.get("/daily-sales/", {
//         params: {
//           page: pageNumber,
//           date: selectedDate,
//           year: selectedYear?.id,
//         },
//       });

//       setSales(res.data.results || []);
//       setTotalPages(Math.ceil((res.data.count || 0) / 8));
//     } catch (err) {
//       console.log(err);
//       Swal.fire({
//         icon: "error",
//         title: "Error",
//         text: "Failed to fetch daily sales.",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ================= GENERATE REPORT =================
//   const generateReport = async () => {
//     setLoading(true);

//     try {
//       const res = await api.post("/daily-sales/generate/", {
//         year: selectedYear?.id,
//       });

//       Swal.fire({
//         icon: "success",
//         title: "Success",
//         text: res.data.message,
//         confirmButtonColor: "#b8860b",
//       });

//       await checkStatus();
//       await fetchSales(1, date);
//       setPage(1);
//     } catch (err) {
//       Swal.fire({
//         icon: "warning",
//         title: "No Sales",
//         text:
//           err.response?.data?.message ||
//           "No sales found for selected year!",
//       });

//       console.log(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ================= PRINT REPORT =================
//   const printDailySales = async () => {
//     try {
//       setLoading(true);

//       const res = await api.get("/daily-sales/", {
//         params: {
//           date,
//           year: selectedYear?.id,
//           page_size: 100000,
//         },
//       });

//       const reportSales = res.data.results || [];

//       const allItems = [];
//       reportSales.forEach((sale) => {
//         sale.items?.forEach((item) => {
//           allItems.push(item);
//         });
//       });

//       if (allItems.length === 0) {
//         Swal.fire({
//           icon: "warning",
//           title: "No Sales Found",
//           text: `No sales records found for ${date}`,
//         });
//         return;
//       }

//       const purchasedBy =
//         allItems[0]?.purchased_by_name || "Multiple Customers";

//       const blob = await pdf(
//         <DailySalesPDFDocument
//           sales={allItems}
//           selectedDate={date}
//           purchasedBy={purchasedBy}
//         />
//       ).toBlob();

//       const blobUrl = URL.createObjectURL(blob);
//       window.open(blobUrl, "_blank");

//       Swal.fire({
//         icon: "success",
//         title: "PDF Ready",
//         text: "Daily sales report generated successfully.",
//         timer: 2000,
//         showConfirmButton: false,
//       });
//     } catch (err) {
//       console.log(err);
//       Swal.fire({
//         icon: "error",
//         title: "Error",
//         text: "Failed to generate daily sales PDF.",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ================= EFFECTS =================
//   useEffect(() => {
//     checkStatus();
//   }, []);

//   useEffect(() => {
//     if (!selectedYear) return;

//     setPage(1);
//     fetchSales(1, date);
//     // eslint-disable-next-line
//   }, [selectedYear, date]);

//   useEffect(() => {
//     fetchSales(page, date);
//     // eslint-disable-next-line
//   }, [page]);

//   return (
//     <Card className="daily-sales-card">

//       {/* TITLE */}
//       <Typography variant="h5" className="title">
//         Daily Sales Report - {selectedYear?.name}
//       </Typography>

//       {/* CONTROLS */}
//       <Grid container spacing={2} className="controls">

//         {/* GENERATE REPORT (HIDDEN FOR OWNER) */}
//         {canGenerateReport && (
//           <Grid item>
//             <Button
//               variant="contained"
//               onClick={generateReport}
//               disabled={isLocked}
//             >
//               {isLocked ? "Report Locked" : "Generate Report"}
//             </Button>
//           </Grid>
//         )}

//         {/* DATE */}
//         <Grid item>
//           <TextField
//             type="date"
//             value={date}
//             onChange={(e) => {
//               setDate(e.target.value);
//               setPage(1);
//             }}
//             size="small"
//           />
//         </Grid>

//         {/* VIEW RECORDS */}
//         <Grid item>
//           <Button
//             variant="outlined"
//             onClick={() => fetchSales(1, date)}
//           >
//             View Records
//           </Button>
//         </Grid>

//         {/* PRINT */}
//         <Grid item>
//           <Button
//             variant="contained"
//             color="success"
//             onClick={printDailySales}
//           >
//             Print Daily Report
//           </Button>
//         </Grid>
//       </Grid>

//       {/* LOADING */}
//       {loading ? (
//         <div className="loader">
//           <CircularProgress />
//         </div>
//       ) : (
//         <>
//           {/* TABLE */}
//           <div className="table-responsive">
//             <Table className="daily-sales-table">
//               <TableHead>
//                 <TableRow>
//                   <TableCell>#</TableCell>
//                   <TableCell>Product</TableCell>
//                   <TableCell>Qty</TableCell>
//                   <TableCell>Sold By</TableCell>
//                   <TableCell>Unit Price</TableCell>
//                   <TableCell>Total</TableCell>
//                   <TableCell>Date</TableCell>
//                 </TableRow>
//               </TableHead>

//               <TableBody>
//                 {sales.length > 0 ? (
//                   sales.map((sale) =>
//                     sale.items?.map((item, index) => (
//                       <TableRow key={item.id}>
//                         <TableCell>
//                           {(page - 1) * 8 + index + 1}
//                         </TableCell>

//                         <TableCell>{item.product_name}</TableCell>
//                         <TableCell>{item.quantity_taken}</TableCell>
//                         <TableCell>{item.purchased_by_name}</TableCell>

//                         <TableCell>
//                           ${formatMoney(item.unit_price)}
//                         </TableCell>

//                         <TableCell>
//                           ${formatMoney(item.total_price)}
//                         </TableCell>

//                         <TableCell>
//                           {dayjs(item.created_at).format("YYYY-MM-DD")}
//                         </TableCell>
//                       </TableRow>
//                     ))
//                   )
//                 ) : (
//                   <TableRow>
//                     <TableCell colSpan={7} align="center">
//                       No records found for {date}
//                     </TableCell>
//                   </TableRow>
//                 )}
//               </TableBody>
//             </Table>
//           </div>

//           {/* PAGINATION */}
//           <div className="pagination-container">
//             <Pagination
//               count={totalPages}
//               page={page}
//               onChange={(e, value) => setPage(value)}
//             />
//           </div>
//         </>
//       )}
//     </Card>
//   );
// };

// export default DailySales;

